import {
  CivilStatus,
  Employment,
  EmploymentRelationship,
  Frequency,
  FuturePlan,
  Gender,
  Insurance,
  InvestorProfile,
  Loan,
  MonthlyExpenditure,
  SavingOrInvestment,
} from '@core/constants/data-types.constants';
import { CurrencyAmount } from '@core/models/currency-amount.model';
import {
  LiteResponsesWithLimits,
  LiteResponsesWithLimitsPerInsuranceTarget,
} from '@core/services/lite.service';
import { parseISO } from 'date-fns';
import { getInsuranceTarget, LifePlanInsurances } from './insurance.model';
import { updateProductWithLiteValuesAndLimits } from './product.model';
import { IdentityDocument } from './identity-document.model';

export interface SimplePerson {
  id: string;
  name?: string;
  age?: number;
  university?: string;
  universityCost?: CurrencyAmount;
}

export interface Person {
  personal?: PersonalData;
  professional?: ProfessionalData;
  financial?: FinancialData;
  insurances?: InsurancesData;
}

export interface PersonalData {
  name?: string;
  surnames?: string;
  birthDate?: string;
  gender?: Gender;
  civilStatus?: CivilStatus;
  email?: string;
  phone?: string;
  identityDocument?: IdentityDocument;
  children?: SimplePerson[];
  dependantPeople?: SimplePerson[];
}

export interface ProfessionalData {
  employmentRelationship?: EmploymentRelationship;
  employment?: Employment;
  contributionYears?: number;
}

export interface FinancialData {
  netMonthlyIncome?: CurrencyAmount;
  otherMonthlyIncome?: CurrencyAmount;
  monthlyExpenditureType?: 'general' | 'detailed';
  generalMonthlyExpenditure?: CurrencyAmount;
  detailedMonthlyExpenditure?: {
    type: MonthlyExpenditure;
    amount: CurrencyAmount;
  }[];
  savingsAndInvestments?: {
    type: SavingOrInvestment;
    amount: CurrencyAmount;
  }[];
  investorProfile?: InvestorProfile;
  futurePlans?: FuturePlanData[];
  loans?: {
    type: Loan;
    pendingAmount: CurrencyAmount;
    monthlyFee: CurrencyAmount;
    remainingYears: number;
  }[];
}

export interface FuturePlanData {
  id: string;
  type: FuturePlan;
  goalAmount: CurrencyAmount;
  assuredAmount: CurrencyAmount;
  savingAmount: CurrencyAmount;
  startDate: string;
  goalDate: string;
}

export type SimpleInsurance = {
  type: Insurance;
  assuredAmount: CurrencyAmount;
  monthlyFee?: CurrencyAmount;
};
export interface InsurancesData {
  currentInsurances?: SimpleInsurance[];
}

export type LifePlanType = Insurance | 'COMPLETE';
export type LifePlanStatus = 'DRAFT' | 'NORMAL' | 'SENT';

export interface LifePlan {
  id?: string;
  agentIdClientId?: string;
  clientId?: string;
  createdDatetime?: string;
  modifiedDatetime?: string;
  title?: string;
  type: LifePlanType;
  status: LifePlanStatus;
  user?: Person;
  spouse?: Person;
  insurances?: LifePlanInsurances;
  canBeSentToAgent?: boolean;
  selectedProductId?: string;
  frequency?: Frequency;
}

export type LifePlanProjection = LifePlan & { insured?: string };

export const DefaultLifePlan: LifePlan = {
  type: 'COMPLETE',
  status: 'DRAFT',
};

export function getCurrentAge(birthDate: string): number {
  const timeDiff = Math.abs(Date.now() - parseISO(birthDate).getTime());
  const age = Math.floor(timeDiff / (1000 * 3600 * 24) / 365.25);
  return age;
}

export function updateLifePlanInsuranceTargetCurrentProductWithLiteValuesAndLimits(
  lifePlan: LifePlan,
  insuranceTargetId: string,
  productId: string,
  liteResponses: LiteResponsesWithLimits
) {
  const insuranceTarget = getInsuranceTarget(lifePlan, insuranceTargetId);
  if (insuranceTarget?.currentProduct?.id === productId) {
    const product = insuranceTarget.currentProduct;
    updateProductWithLiteValuesAndLimits(product, liteResponses);
  }
}

export function updateLifePlanInsuranceTargetsCurrentProductsWithLiteValuesAndLimits(
  lifePlan: LifePlan,
  responses: LiteResponsesWithLimitsPerInsuranceTarget[]
) {
  responses.forEach((response) => {
    updateLifePlanInsuranceTargetCurrentProductWithLiteValuesAndLimits(
      lifePlan,
      response.insuranceTarget.id,
      response.insuranceTarget.currentProduct?.id || '',
      response.liteResponses
    );
  });
}

export function deepCloneLifePlan(lifePlan: LifePlan): LifePlan {
  // TODO: use a better approach
  return JSON.parse(JSON.stringify(lifePlan));
}
